import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Alert,
    LinearProgress,
    Box,
    Typography
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, CheckCircle } from "@mui/icons-material";
import { LoginContext } from "../contexts/LoginContext";
import { User } from "../Models/User";
import useAxios from "../hooks/useAxios";
import { ACTIVATE_URL, PASSWORD_REG_EXPRESSION, PASSWORD_REQUIREMENTS_MESSAGE } from "../utils/constant";
import '../styles/Login.css';

const ActivatePage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const navigate = useNavigate();
    const { postWithAxios } = useAxios();
    const { setCurrentUser } = useContext(LoginContext);

    // Form state
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Error state
    const [error, setError] = useState({ password: "", confirmPassword: "" });
    const [globalError, setGlobalError] = useState("");
    const [globalSuccess, setGlobalSuccess] = useState("");

    // Token validation
    const [isTokenValid, setIsTokenValid] = useState(true);

    useEffect(() => {
        if (!token) {
            setIsTokenValid(false);
            setGlobalError("Lien d'activation invalide ou manquant.");
        }
    }, [token]);

    /**
     * Calcule la force du mot de passe (0-100)
     */
    const getPasswordStrength = (pwd: string): number => {
        if (!pwd) return 0;

        let strength = 0;

        // Longueur minimum (8 chars)
        if (pwd.length >= 8) strength += 20;

        // Au moins une minuscule
        if (/[a-z]/.test(pwd)) strength += 20;

        // Au moins une majuscule
        if (/[A-Z]/.test(pwd)) strength += 20;

        // Au moins un chiffre
        if (/\d/.test(pwd)) strength += 20;

        // Au moins un caractère spécial
        if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 20;

        return strength;
    };

    /**
     * Retourne la couleur selon la force
     */
    const getPasswordStrengthColor = (strength: number): "error" | "warning" | "info" | "success" => {
        if (strength < 40) return "error";
        if (strength < 60) return "warning";
        if (strength < 100) return "info";
        return "success";
    };

    /**
     * Retourne le label selon la force
     */
    const getPasswordStrengthLabel = (strength: number): string => {
        if (strength === 0) return "";
        if (strength < 40) return "Faible";
        if (strength < 60) return "Moyen";
        if (strength < 100) return "Bon";
        return "Excellent";
    };

    const passwordStrength = getPasswordStrength(password);

    const validateForm = () => {
        const newError = {
            password: !PASSWORD_REG_EXPRESSION.test(password)
                ? PASSWORD_REQUIREMENTS_MESSAGE
                : "",
            confirmPassword: password !== confirmPassword
                ? "Les mots de passe ne correspondent pas"
                : "",
        };
        setError(newError);
        return !newError.password && !newError.confirmPassword;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        setGlobalError("");
        setGlobalSuccess("");

        try {
            const response = await postWithAxios(ACTIVATE_URL, {
                token,
                password
            });

            // Connexion automatique après activation
            if (response.user && response.accessToken && response.refreshToken) {
                setCurrentUser(
                    new User({
                        uuid: response.user.uuid,
                        email: response.user.email,
                        role: response.user.role,
                        isActivated: true,
                        firstname: response.user.firstname,
                        lastname: response.user.lastname,
                        projects: response.user.projects || [],
                        defaultProject: response.user.defaultProject,
                        tokens: {
                            accessToken: response.accessToken,
                            refreshToken: response.refreshToken
                        }
                    })
                );

                setGlobalSuccess("Compte activé avec succès ! Redirection en cours...");

                // Redirection selon le rôle
                setTimeout(() => {
                    if (response.user.role === "ADMIN") {
                        navigate("/admin/dashboard");
                    } else {
                        navigate("/user/dashboard");
                    }
                }, 1500);
            } else {
                // Fallback si pas de tokens (ne devrait pas arriver)
                setGlobalSuccess("Compte activé avec succès !");
                setTimeout(() => navigate("/"), 2000);
            }
        } catch (err: any) {
            const errorMessage = err.message || "Une erreur est survenue lors de l'activation.";
            setGlobalError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !isLoading) {
            handleSubmit();
        }
    };

    // Si le token est invalide, afficher un message d'erreur
    if (!isTokenValid) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h1 className="login-title">Lien invalide</h1>
                    </div>
                    <Alert severity="error" className="error-alert">
                        {globalError || "Le lien d'activation est invalide ou a expiré."}
                    </Alert>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        className="login-btn"
                        onClick={() => navigate("/")}
                        style={{ marginTop: "1rem" }}
                    >
                        Retour à la connexion
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1 className="login-title">Activez votre compte</h1>
                    <p className="login-subtitle">
                        Définissez votre mot de passe pour activer votre compte et commencer à utiliser l'application.
                    </p>
                </div>

                {globalError && (
                    <Alert severity="error" className="error-alert">
                        {globalError}
                    </Alert>
                )}

                {globalSuccess && (
                    <Alert severity="success" className="error-alert" icon={<CheckCircle />}>
                        {globalSuccess}
                    </Alert>
                )}

                {!globalSuccess && (
                    <>
                        <div className="form-group" onKeyDown={handleKeyDown}>
                            <TextField
                                label="Nouveau mot de passe"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                error={!!error.password}
                                helperText={error.password}
                                disabled={isLoading}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                disabled={isLoading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Indicateur de force du mot de passe */}
                            {password && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Force du mot de passe
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: passwordStrength < 40 ? 'error.main' :
                                                    passwordStrength < 60 ? 'warning.main' :
                                                        passwordStrength < 100 ? 'info.main' : 'success.main'
                                            }}
                                        >
                                            {getPasswordStrengthLabel(passwordStrength)}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={passwordStrength}
                                        color={getPasswordStrengthColor(passwordStrength)}
                                        sx={{
                                            height: 8,
                                            borderRadius: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                </Box>
                            )}

                            <TextField
                                label="Confirmer le mot de passe"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                error={!!error.confirmPassword}
                                helperText={error.confirmPassword}
                                disabled={isLoading}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                                disabled={isLoading}
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                style={{ marginTop: "1rem" }}
                            />
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className="login-btn"
                            onClick={handleSubmit}
                            disabled={isLoading || !password || !confirmPassword}
                        >
                            {isLoading ? "Activation en cours..." : "Activer mon compte"}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ActivatePage;